import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Col,
  Container,
  Breadcrumbs,
  Row,
  PageContent,
  BoxList,
  Tags,
  BadgeList,
} from 'components';
import { isEmpty } from 'lodash';
import { Margin } from 'styled-components-spacing';
import { loadProfileCompletedCoursesCreator, loadUserBadgesCreator } from 'store/actionsCreators';
import { globalPropTypes, getMembershipLabel } from 'helpers';
import {
  ProfileSection,
  CertificationsStatus,
  Details,
  Description,
  UserOverview,
} from './components';
import { Content, ContentPadding } from './Profile.styled';
import { formatDate } from 'helpers/date';

const EMPTY_CHAR = '-';
const breadcrumbs = [
  {
    title: 'navigation.breadcrumbs.user.profile',
    url: '/user/profile',
  },
];

function Profile({
  user,
  certifications,
  badges,
  loadUserBadges,
  loadProfileCompletedCourses,
  completedCourses,
}) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({});

  useEffect(() => {
    loadUserBadges();
    loadProfileCompletedCourses();
  }, []);

  useEffect(() => {
    const profileData = {
      avatar: user.userProfile && user.userProfile.photo,
      userName: `${user.firstName} ${user.lastName}`,
      phoneNumber: (user.userProfile && user.userProfile.telephone) || undefined,
      professionDetails: [
        [
          t('page.user.profile.professionalTitle'),
          (user.userProfile &&
            user.userProfile.proffesionalTitle &&
            user.userProfile.proffesionalTitle.name) ||
            EMPTY_CHAR,
        ],
        [
          t('page.user.profile.jobTitle'),
          (user.userProfile && user.userProfile.jobTitle && user.userProfile.jobTitle.name) ||
            EMPTY_CHAR,
        ],
        [
          t('page.user.profile.company'),
          (user.userProfile && user.userProfile.company) || EMPTY_CHAR,
        ],
        [
          t('page.user.profile.industry'),
          (user.userProfile && user.userProfile.industry && user.userProfile.industry.name) ||
            EMPTY_CHAR,
        ],
      ],
      additionalInfo: [
        [t('page.user.profile.myIpmaNumber'), user.id || EMPTY_CHAR],
        // The number to quote to a Member Association, and the one printed on the membership
        // badge. Shown next to the MyIPMA number because people reach for the wrong one otherwise.
        [t('page.user.profile.maMemberId'), user.maMemberId || EMPTY_CHAR],
        [t('page.user.profile.email'), user.email || EMPTY_CHAR],
        [
          t('page.user.profile.dob'),
          (user.dob && formatDate(user.dob)) || EMPTY_CHAR,
        ],
        [
          t('page.user.profile.gender'),
          (user.userProfile && user.userProfile.gender) || EMPTY_CHAR,
        ],
        [
          t('page.user.profile.countryOfOrigin'),
          (user.userProfile &&
            user.userProfile.countryOfOrigin &&
            user.userProfile.countryOfOrigin.name) ||
            EMPTY_CHAR,
        ],
        [t('page.user.profile.liveNow'), (user.country && user.country.name) || EMPTY_CHAR],
        [
          t('page.user.profile.ma'),
          getMembershipLabel({
            status: user.memberAssociationPeople && user.memberAssociationPeople.status,
            orgName: user.memberAssociation && user.memberAssociation.name,
            orgType: 'MA',
            t,
          }) || EMPTY_CHAR,
        ],
        // Its own row, and only when there is one: an honorary affiliation is an addition to the
        // membership above, never a replacement for it.
        user.honoraryMemberAssociationPeople && user.honoraryMemberAssociationPeople.status
          ? [
              t('page.user.profile.honoraryMa'),
              getMembershipLabel({
                status: user.honoraryMemberAssociationPeople.status,
                orgName: user.honoraryMemberAssociation && user.honoraryMemberAssociation.name,
                orgType: 'MA',
                t,
                honorary: true,
              }) || EMPTY_CHAR,
            ]
          : [],
        user.yCPeople && user.yCPeople.status
          ? [
              t('page.user.profile.youngCrewMember'),
              getMembershipLabel({
                status: user.yCPeople.status,
                orgName: user.youngCrew && user.youngCrew.name,
                orgType: 'YC',
                t,
              }) || EMPTY_CHAR,
            ]
          : [],
      ],
      shortBio: (user.userProfile && user.userProfile.shortBio) || EMPTY_CHAR,
      tags:
        user.userProfile && user.userProfile.tags && user.userProfile.tags.length
          ? user.userProfile.tags.map((item) => item.name)
          : [],
      profileRoles: user.profileRoles,
    };

    setProfile(profileData);
  }, [user]);

  const renderBadges = () => {
    const badgesList = Object.keys(badges)
      .map((key) => badges[key].filter((el) => el.type === 'accepted'))
      .flat();

    return (
      <ProfileSection
        displayer={BadgeList}
        heading={t('page.user.dashboard.badgesTitle')}
        items={badgesList}
        share
        alert={t('global.noResultsBadges')}
        showLimit={6}
      />
    );
  };

  return (
    <PageContent>
      <Container>
        <Breadcrumbs items={breadcrumbs} />
        <Content>
          <Row>
            <Col size={{ xs: 1, lg: 1 / 6 }}>
              <Margin bottom={{ xs: 32, lg: 0 }}>
                <UserOverview
                  thumbnail={profile.avatar}
                  name={profile.userName}
                  phone={profile.phoneNumber}
                  showShareButton
                />
              </Margin>
            </Col>
            <Col size={{ xs: 1, lg: 5 / 6 }}>
              <ContentPadding>
                <Row>
                  <Col size={{ md: 2 / 5 }}>
                    <ProfileSection
                      displayer={Details}
                      heading={t('page.user.profile.professionDetails')}
                      details={profile.professionDetails}
                    />
                    <ProfileSection
                      displayer={Details}
                      heading={t('page.user.profile.additionalInfo')}
                      details={profile.additionalInfo}
                    />
                    {!isEmpty(profile.profileRoles) ? (
                      <ProfileSection
                        displayer={Details}
                        heading={t('page.user.profile.roles')}
                        details={profile.profileRoles}
                      />
                    ) : null}
                  </Col>
                  <Col size={{ md: 3 / 5 }}>
                    <ProfileSection
                      displayer={Description}
                      heading={t('page.user.profile.shortBio')}
                      text={profile.shortBio}
                    />
                    <ProfileSection
                      displayer={Tags}
                      heading={t('page.user.profile.tags')}
                      tags={profile.tags}
                    />
                  </Col>
                </Row>
                <ProfileSection
                  displayer={CertificationsStatus}
                  heading={t('page.user.profile.certificationStatus')}
                  certs={certifications}
                />
                <ProfileSection
                  displayer={BoxList}
                  heading={t('page.user.profile.coursesTitle')}
                  type="course"
                  items={completedCourses}
                  alert={t('global.noResultsCourses')}
                />
                {renderBadges()}
              </ContentPadding>
            </Col>
          </Row>
        </Content>
      </Container>
    </PageContent>
  );
}

Profile.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maMemberId: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    dob: PropTypes.string,
    memberAssociation: PropTypes.any,
    honoraryMemberAssociation: PropTypes.any,
    youngCrew: PropTypes.any,
    memberAssociationPeople: PropTypes.shape({ status: PropTypes.string }),
    honoraryMemberAssociationPeople: PropTypes.shape({ status: PropTypes.string }),
    yCPeople: PropTypes.shape({ status: PropTypes.string }),
    country: PropTypes.shape({ name: PropTypes.number.isRequired }),
    youngCrewMember: PropTypes.bool,
    profileRoles: PropTypes.shape([]),
    uniqueUrl: PropTypes.string,
    userProfile: PropTypes.shape({
      company: PropTypes.string,
      countryOfOrigin: PropTypes.shape({ name: PropTypes.string.isRequired }),
      industry: PropTypes.shape({ name: PropTypes.string.isRequired }),
      telephone: PropTypes.string,
      proffesionalTitle: PropTypes.shape({ name: PropTypes.number.isRequired }),
      jobTitle: PropTypes.shape({ name: PropTypes.number.isRequired }),
      tags: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.number.isRequired })),
      profilePrivacy: PropTypes.shape({ name: PropTypes.number.isRequired }),
      gender: PropTypes.string,
      shortBio: PropTypes.string,
      photo: PropTypes.string,
    }),
  }).isRequired,
  certifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      name: PropTypes.string,
      awardedDate: PropTypes.string,
      validityDate: PropTypes.string,
      ma: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
      level: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
  ),
  completedCourses: PropTypes.shape({}).isRequired,
  loadUserBadges: PropTypes.func.isRequired,
  loadProfileCompletedCourses: PropTypes.func.isRequired,
  profileRoles: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)),
  badges: globalPropTypes.BadgesPropsTypes,
};

Profile.defaultProps = {
  certifications: [],
  profileRoles: [],
  badges: {},
};

const mapStateToProps = ({ user }) => ({
  user: user.user,
  certifications: user.certifications,
  completedCourses: user.profileCompletedCurses,
  badges: user.badges,
});

const mapDispatchToProps = (dispatch) => ({
  loadUserBadges: () => dispatch(loadUserBadgesCreator()),
  loadProfileCompletedCourses: () => dispatch(loadProfileCompletedCoursesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Profile);
